// Configuration Entra External ID (CIAM) pour cc-jaw.
// App registrations créées par cc-armor-entra/.../create-jaw-app-registrations.ps1 :
//   web    -> cc-attack-jaw-spa-react     (PKCE navigateur)
//   natif  -> cc-attack-jaw-mobile-react  (react-native-app-auth, schéma ccjaw://)
// Le backend cc-attack expose le scope api://cc-attack-backend-dev/Stories.Read
// et les endpoints /me, /me/stories, /public/marketplace/stories.

import { Platform } from 'react-native';

import { getEnvConfig, resolveApiBase } from '../config/env';

export interface EntraConfig {
  tenantId: string;
  tenantSubdomain: string;
  clientId: string;
  scope: string; // scope API délégué (en plus de openid/profile/email/offline_access)
  redirectUri: string;
  postLogoutUri: string;
  apiBaseUrl: string;
}

// Source UNIQUE : la config est résolue par env.ts (détection à l'exécution
// selon le hostname / override). Aucune valeur d'env hardcodée ici.
const ENV = getEnvConfig();

export function getEntraConfig(): EntraConfig {
  const isWeb = Platform.OS === 'web';
  const origin =
    isWeb && typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'http://localhost:8080';

  return {
    tenantId: ENV.AUTH_TENANT_ID,
    tenantSubdomain: ENV.AUTH_TENANT_SUBDOMAIN,
    scope: ENV.AUTH_API_SCOPE,
    apiBaseUrl: resolveApiBase(),
    clientId: isWeb ? ENV.AUTH_SPA_CLIENT_ID : ENV.AUTH_MOBILE_CLIENT_ID,
    redirectUri: isWeb ? `${origin}/auth/callback` : 'ccjaw://auth/callback',
    postLogoutUri: isWeb ? `${origin}/logout/callback` : 'ccjaw://logout/callback',
  };
}

export const AUTHORITY = `https://${ENV.AUTH_TENANT_SUBDOMAIN}.ciamlogin.com/${ENV.AUTH_TENANT_ID}`;
export const SCOPES = ['openid', 'profile', 'email', 'offline_access', ENV.AUTH_API_SCOPE];

// Mappe les claims d'un access/id token Entra vers le profil utilisateur de l'app.
export interface EntraClaims {
  sub?: string;
  oid?: string;
  email?: string;
  preferred_username?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
}

export function claimsToUser(claims: EntraClaims) {
  const email = claims.email || claims.preferred_username || '';
  let prenom = claims.given_name || '';
  let nom = claims.family_name || '';
  if (!prenom && !nom && claims.name) {
    const parts = claims.name.trim().split(/\s+/);
    prenom = parts[0] || '';
    nom = parts.slice(1).join(' ');
  }
  return {
    id: claims.oid || claims.sub || email || 'unknown',
    prenom,
    nom,
    email,
    phone: '',
  };
}

// Décodage base64 pur-JS (pas de dépendance à Buffer/atob) — web + natif.
function base64Decode(input: string): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const str = input.replace(/=+$/, '');
  let output = '';
  let bc = 0;
  let bs = 0;
  for (let i = 0; i < str.length; i++) {
    const idx = chars.indexOf(str.charAt(i));
    if (idx === -1) continue;
    bs = bc % 4 ? bs * 64 + idx : idx;
    if (bc++ % 4) {
      output += String.fromCharCode(255 & (bs >> ((-2 * bc) & 6)));
    }
  }
  return output;
}

export function decodeJwt(token: string): EntraClaims {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const binary = base64Decode(payload);
    let json: string;
    try {
      json = decodeURIComponent(escape(binary)); // restitue l'UTF-8
    } catch {
      json = binary;
    }
    return JSON.parse(json);
  } catch {
    return {};
  }
}
